import { Component, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { IProject, IProjectListData } from '@static/models/project';
import { AuthService } from '@static/services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { ProjectService } from '@shared/services/project.service';
import { ConfirmComponent } from '@shared/dialogs/confirm/confirm.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SnackService } from '@static/services/snack.service';
import { ObservableService } from '@static/services/observable.service';
import { FillHeightDirective } from '@static/directives/fill-height.directive';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { ProjectListComponent } from './project-list/project-list.component';
import { IProjectUploadData, ProjectUploadComponent } from '@shared/dialogs/project-upload/project-upload.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ProjectPortfolioComponent } from './project-portfolio/project-portfolio.component';
import { PortfolioService } from '@shared/services/portfolio.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { StorageService } from '@static/services/storage.service';
import { KEY_PROJECT_FILTER } from '@static/storage-keys';
import { ProjectCodeComponent } from './project-code/project-code.component';
import { MatBadgeModule } from '@angular/material/badge';
import { ProjectSettingsDialogComponent } from '@shared/dialogs/project-settings/project-settings-dialog.component';

interface IProjectFilter {
  name: string;
  codes: number[];
  codesMode: 'AND' | 'OR';
}

@Component({
  selector: 'app-projects',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FillHeightDirective,
    MatInputModule,
    MatFormFieldModule,
    MatSidenavModule,
    MatBadgeModule,
    ReactiveFormsModule,
    ProjectListComponent,
    ProjectPortfolioComponent,
  ],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss',
  providers: [
    ObservableService,
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
  ]
})
export class ProjectsComponent {
  private _observableService: ObservableService = inject(ObservableService);
  private _router: Router = inject(Router);
  private _projectService: ProjectService = inject(ProjectService);
  private _authService: AuthService = inject(AuthService);
  private _dialog: MatDialog = inject(MatDialog);
  private _snackService: SnackService = inject(SnackService);
  protected _portfolioService: PortfolioService = inject(PortfolioService);
  private _storageService: StorageService = inject(StorageService);

  public projectUploadControl: FormControl;

  public isSiteAdmin: boolean;
  public myProjectList: IProject[];
  public projectList: IProject[];
  public archiveList: IProject[];

  protected selectedPortfolio?: number;
  protected selectedPortfolioName?: string;

  protected filterForm: FormGroup;
  protected hasProjectCodes: boolean;
  protected projectCodeCount: number;

  constructor() {
    this.projectUploadControl = new FormControl();
    this.projectCodeCount = 0;
    this.isSiteAdmin = this._authService.isSiteAdmin;

    this.filterForm = new FormGroup({
      'name': new FormControl('', { updateOn: 'submit' }),
      'codes': new FormControl(),
      'codesMode': new FormControl('AND'),
    });
    // Load saved filters
    const res = this._storageService.loadData(KEY_PROJECT_FILTER, false);
    if (res) {
      const data = JSON.parse(res) as IProjectFilter;
      this.filterForm.controls['name'].setValue(data.name);
      this.filterForm.controls['codes'].setValue(data.codes);
      this.filterForm.controls['codesMode'].setValue(data.codesMode ? data.codesMode : 'AND');
      this.projectCodeCount = data.codes.length;
    }

    this.hasProjectCodes = this._projectService.getProjectCodes().projectCats.length > 0;

    this._observableService.subscribe(
      this.filterForm.valueChanges,
      () => {
        this._saveFilter();
        this._setProjectLists();
        this.projectCodeCount = this.filterForm.controls['codes'].value.length;
      }
    );

    this.myProjectList = [];
    this.projectList = [];
    this.archiveList = [];
    this._setProjectLists();

    // Listen for project updates
    this._observableService.subscribe(
      this._projectService.projectListUpdated,
      () => {
        this._setProjectLists();
      }
    )

    this.selectedPortfolioName = this._projectService.selectedPortfolioName;

    // Listen to selected portfolio updates
    this._observableService.subscribe(
      this._portfolioService.selectedUpdated,
      (res: number | undefined) => {
        this.selectedPortfolio = res;
        if (res) this.selectedPortfolioName = this._projectService.selectedPortfolioName;
        this._setProjectLists();
      }
    );

    // Listen to portfolio updates
    this._observableService.subscribe(
      this._portfolioService.portfoliosUpdated,
      () => {
        this.selectedPortfolioName = this._projectService.selectedPortfolioName;
        this._setProjectLists();
      }
    );
  }

  /**
   * Open the identified project information and tasks
   *
   * @param projId  project
   */
  public openProjectInfo(proj: IProject): void {
    this._projectService.isPortfolio.set(false);
    this._projectService.currentId = proj.proj_id;
    this._router.navigate([`/activities`]);
  }

  /**
   * Open the identified project information and tasks
   */
  public openPortfolioInfo(): void {
    this._projectService.isPortfolio.set(true);
    this._projectService.currentId = `p${this._portfolioService.selectedPortfolio()}`;
    this._router.navigate([`/activities`]);
  }

  /**
   * Sort the lists of projects
   */
  private _setProjectLists(): void {
    this.myProjectList = [];
    this.projectList = [];
    this.archiveList = [];

    // Filter the project list into groups
    const nameFilter = (this.filterForm.controls['name'].value as string)?.toLowerCase();
    const codeFilter = (this.filterForm.controls['codes'].value as number[]) ?? [];
    const codeMode = (this.filterForm.controls['codesMode'].value as 'AND' | 'OR') ?? '';
    const codeMap = this._projectService.getProjectCodes().projectCats;
    this.hasProjectCodes = codeMap.length > 0;
    this._projectService.projectList
      .filter((p: IProject) => {
        const projCodes = new Set(codeMap.filter(c => c.proj_id === p.proj_id).map(p => p.proj_catg_id));
        const passName = (!nameFilter
                          || (nameFilter && (p.proj_full_name && p.proj_full_name.toLowerCase().includes(nameFilter))
                              || (p.proj_short_name && p.proj_short_name.toLowerCase().includes(nameFilter))));
        const passCode = codeFilter.length === 0 || ((codeMode === 'AND' && codeFilter.every(i => projCodes.has(i)))
                                                    || (codeMode === 'OR' && codeFilter.some(i => projCodes.has(i))));
        return passName && passCode;
      }
      )
      .forEach((p: IProject) => {
        if (this.selectedPortfolio !== undefined && !p.portfolios.includes(this.selectedPortfolio)) return;
        // Archived
        if (this._projectService.isProjectArchived(p.proj_id)()) this.archiveList.push(p);
        else {
          // Owned project
          if (this._projectService.isProjectOwner(p.proj_id, this._authService.email)) this.myProjectList.push(p);
          // Other project
          else this.projectList.push(p)
        }
      });
  }

  /**
   * Open the project codes filter dialog
   */
  protected openProjectCodes(): void {
    this._dialog.open(ProjectCodeComponent, {
      minWidth: '75vw',
      data: {
        codes: this.filterForm.controls['codes'],
        codesMode: this.filterForm.controls['codesMode']
      }
    });
  }

  /**
   * Save the project filter state
   */
  private _saveFilter(): void {
    const data = {
      name: this.filterForm.controls['name'].value ?? '',
      codes: this.filterForm.controls['codes'].value ?? [],
      codesMode: this.filterForm.controls['codesMode'].value ?? '',
    } as IProjectFilter;
    this._storageService.saveData(KEY_PROJECT_FILTER, JSON.stringify(data), false);
  }

  /**
   * Upload a project file for processing
   *
   * @param event
   * @returns
   */
  public projectFileUpload(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files) return;

    // Extract and build form
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('user', this._authService.email ?? '');

    // Post and wait for upload
    const dialog = this._dialog.open(ProjectUploadComponent, {
      disableClose: true,
      data: {
        formData,
        dataCallback: this._appendProjects.bind(this)
      } as IProjectUploadData<IProjectListData>
    });

    dialog.afterClosed().subscribe((loadedProjects: IProjectListData) => {
      this.projectUploadControl.reset();
      // Open the project config if there is only one
      if (loadedProjects !== undefined && loadedProjects && loadedProjects.projectInfo.length === 1)
        this.openProjectSettings(loadedProjects.projectInfo[0]);
    });
  }

  /**
   * Toggle the project lock
   */
  public toggleProjectLock(project: IProject): void {
    const newState = (project.lock ?? 0) ? 0 : 1;
    this._projectService.toggleLock(project.proj_id, newState).then(() => {
      project.lock = newState;
    });
  }

  /**
   * Append the new projects to the project list
   *
   * @param res projects to add
   */
  private _appendProjects(res: IProjectListData): void {
    this._projectService.appendProjects(res.projectInfo);
    this._projectService.appendProjectSettings(res.projectSettings);
    this._setProjectLists();
  }

  /**
   * Open the project settings
   *
   * @param project project ref
   */
  public openProjectSettings(project: IProject): void {
    this._dialog.open(ProjectSettingsDialogComponent, {
      minWidth: '65vw',
      maxWidth: '90vw',
      data: this._projectService.getProjectSettings(project.proj_id)
    })
  }

  /**
   * Delete the given project
   *
   * @param proj  project to delete
   */
  public deleteProject(proj: IProject): void {
    const msg = `
      <p>Are you sure you want to delete '${proj.proj_short_name}'?</p>
      <p>Deleting a project will remove it from Ctrl key, it can no longer be viewed or modified.</p>
    `;
    const confirm = this._dialog.open(ConfirmComponent, { data: msg });

    this._observableService.subscribe(
      confirm.afterClosed(),
      (res: boolean) => {
        if (res) {
          this._projectService.deleteProject(proj.proj_id).then(() => {
            this._snackService.openInfoSnack('Project deleted');
          }).catch((err: HttpErrorResponse) => {
            if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
              this._snackService.openErrorSnack('Insufficient permissions for this project', err);
            }
            else this._snackService.openErrorSnack('Failed to delete project', err);
          });
        }
      }
    );
  }

  /**
   * Archive the given project
   *
   * @param proj  project to archive
   * @param state archive state
   */
  public archiveProject(proj: IProject, state: number): void {
    const action = state === 1 ? 'archive' : 'unarchive';
    const msg = `Are you sure you want to ${action} '${proj.proj_short_name}'?`;
    const confirm = this._dialog.open(ConfirmComponent, { data: msg });

    this._observableService.subscribe(
      confirm.afterClosed(),
      (res: boolean) => {
        if (res) {
          this._projectService.archiveProject(proj.proj_id, state).then(() => {
            this._snackService.openInfoSnack(`Project ${action}d`);
          }).catch((err: HttpErrorResponse) => {
            if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
              this._snackService.openErrorSnack('Insufficient permissions for this project', err);
            }
            else this._snackService.openErrorSnack(`Failed to ${action} project`, err);
          });
        }
      }
    );
  }

}
